import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, FileText, Users, ShieldCheck, PieChart, MapPin, Fingerprint, ArrowUpRight, CheckCircle2,
} from "lucide-react";
import { Panel, SectionHead, StatusBadge, Row, cellVal, AwaitingSource, useModel, useAri, useSelection } from "./shared";
import { ARI_DOMAIN_TABLES } from "./ari";

const Metric = ({ label, value, sub, testid }) => (
  <Panel className="flex-1" data-testid={testid}>
    <div className="text-[11px] font-bold uppercase text-slate-400">{label}</div>
    <div className="mt-1 text-2xl font-extrabold text-slate-900">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
  </Panel>
);

const DomainCard = ({ icon: Icon, title, tables, to, children }) => {
  const nav = useNavigate();
  return (
    <Panel data-testid={`overview-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 font-bold text-slate-800"><Icon className="h-4 w-4 text-indigo-600" />{title}</div>
        {to && <button onClick={() => nav(to)} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline">Open <ArrowUpRight className="h-3 w-3" /></button>}
      </div>
      {children}
    </Panel>
  );
};

export function CompanyOverviewPage() {
  const model = useModel();
  const company = model.companies?.[0] || "—";
  const reports = model.records.report_instances || [];
  const report = reports[0];

  const exceptions = (model.records.source_rows || []).filter(
    (r) => (model.records.structural_eligibility || []).find((e) => e.source_row_id === r.id)?.classification !== "FINANCIAL_ELIGIBLE"
  ).length;
  const gate = model.records.certification_gate_summary?.[0];
  const latest = reports.slice().sort((a, b) => String(b.financial_year).localeCompare(String(a.financial_year)))[0] || report;
  const identityMissing = true;

  return (
    <div className="space-y-6">
      <SectionHead eyebrow="Company · Certification Studio (v1.1 governance)" title={company}
        caption="This overview is the existing Certification-Studio v1.1 governance surface — NOT the GOVERNED_COMMON_API financial runtime. Its counts and certification status come from the v1.1 governed contract and are never mixed into the governed financial statements." />

      <div className="flex flex-wrap gap-3">
        <Metric testid="ov-reports" label="Annual reports" value={reports.length} sub="in governed corpus" />
        <Metric testid="ov-latest" label="Latest report" value={latest?.financial_year || "—"} sub={latest?.report_id} />
        <Metric testid="ov-rows" label="Financial rows" value={model.counts.source_rows} sub={`${model.counts.source_cells} cells · ${model.counts.source_atoms} atoms`} />
        <Metric testid="ov-integrity" label="Referential integrity" value={<StatusBadge value={model.evidence.referentialIntegrity} />} sub={`${model.evidence.orphanRecordCount} orphans`} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <DomainCard icon={Fingerprint} title="Company identity" tables={ARI_DOMAIN_TABLES.identity}>
          {identityMissing ? (
            <AwaitingSource tables={ARI_DOMAIN_TABLES.identity} testid="ov-identity-awaiting"
              caption="Legal name, identifiers (CIN/PAN/LEI/ISIN) and registered/corporate addresses come from the ari_source identity schema, not yet delivered." />
          ) : (
            <dl className="space-y-1 text-sm"><Row k="Legal name" v={company} /></dl>
          )}
        </DomainCard>

        <DomainCard icon={FileText} title="Latest annual report" to="/reports">
          <dl className="space-y-1 text-sm">
            <Row k="Financial year" v={cellVal(latest?.financial_year)} />
            <Row k="Report id" v={<span className="font-mono text-xs">{cellVal(latest?.report_id)}</span>} />
            <Row k="Statements" v={String((model.records.statement_instances || []).filter((s) => s.report_instance_id === latest?.id).length)} />
            <Row k="Rows on report" v={String((model.records.source_rows || []).filter((r) => r.report_id === latest?.report_id).length)} />
          </dl>
        </DomainCard>

        <DomainCard icon={ShieldCheck} title="Certification" to="/review">
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-slate-400">Gate</dt><dd>{gate ? <StatusBadge value={gate.status} /> : "—"}</dd></div>
            <Row k="Rows pending review" v={String(exceptions)} />
            <div className="flex justify-between"><dt className="text-slate-400">Integrity</dt><dd><StatusBadge value={model.evidence.referentialIntegrity} /></dd></div>
          </dl>
        </DomainCard>

        <DomainCard icon={MapPin} title="Registered & corporate addresses" to="/disclosures">
          <AwaitingSource tables={["addresses", "company_addresses"]} testid="ov-addresses-awaiting"
            caption="Addresses are sourced from the ari_source address schema (not yet delivered)." />
        </DomainCard>

        <DomainCard icon={Users} title="Directors & KMP" to="/governance">
          <AwaitingSource tables={ARI_DOMAIN_TABLES.governance} testid="ov-people-awaiting"
            caption="Unique resolved people (not candidate-block counts) come from the ari_source governance schema." />
        </DomainCard>

        <DomainCard icon={ShieldCheck} title="Auditors" to="/audit">
          <AwaitingSource tables={ARI_DOMAIN_TABLES.audit} testid="ov-audit-awaiting"
            caption="Audit firm, signing partner and opinions come from the ari_source audit schema." />
        </DomainCard>

        <DomainCard icon={PieChart} title="Shareholding summary" to="/shareholding">
          <AwaitingSource tables={ARI_DOMAIN_TABLES.shareholding} testid="ov-shareholding-awaiting"
            caption="Shareholding snapshots and holdings come from the ari_source shareholding schema." />
        </DomainCard>

        <DomainCard icon={FileText} title="Financial summary" to="/financials">
          <dl className="space-y-1 text-sm">
            <Row k="Statements" v={String(model.counts.statement_instances)} />
            <Row k="Source rows" v={String(model.counts.source_rows)} />
            <Row k="Source cells" v={String(model.counts.source_cells)} />
            <div className="flex justify-between"><dt className="text-slate-400">Values status</dt><dd><StatusBadge value="EXTRACTED_RAW" /></dd></div>
          </dl>
        </DomainCard>
      </div>
    </div>
  );
}

export function AnnualReportsPage() {
  const model = useModel();
  const { setYear } = useSelection();
  const nav = useNavigate();
  const reports = model.records.report_instances || [];

  const openReport = (r) => { setYear(r.financial_year); nav("/report-explorer"); };

  return (
    <div>
      <SectionHead eyebrow="Corpus" title="Annual Reports"
        caption="Every governed report by fiscal year, type, source hash and processing completeness. Open a report to explore its stored content — not merely an external link." />
      <Panel>
        <div className="overflow-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm" data-testid="annual-reports-table">
            <thead className="bg-slate-50"><tr>
              {["Financial year", "Report id", "Statements", "Rows", "Cells", "Completeness", "Integrity", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-bold text-slate-700">{h}</th>)}
            </tr></thead>
            <tbody>
              {reports.map((r) => {
                const stmts = (model.records.statement_instances || []).filter((s) => s.report_instance_id === r.id).length;
                const rows = (model.records.source_rows || []).filter((x) => x.report_id === r.report_id).length;
                const cells = (model.records.source_cells || []).filter((c) => (model.records.source_rows || []).some((x) => x.report_id === r.report_id && x.id === c.source_row_id)).length;
                const complete = rows > 0 && stmts > 0;
                return (
                  <tr key={r.id} data-testid="annual-report-row" className="border-b border-slate-100 hover:bg-indigo-50/40">
                    <td className="px-3 py-2 font-semibold text-slate-800">{cellVal(r.financial_year)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-indigo-600">{cellVal(r.report_id)}</td>
                    <td className="px-3 py-2">{stmts}</td>
                    <td className="px-3 py-2">{rows}</td>
                    <td className="px-3 py-2">{cells}</td>
                    <td className="px-3 py-2">{complete ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-4 w-4" />Complete</span> : <StatusBadge value="NOT_CAPTURED" />}</td>
                    <td className="px-3 py-2"><StatusBadge value={model.evidence.referentialIntegrity} /></td>
                    <td className="px-3 py-2 text-right"><button data-testid={`open-report-${r.report_id}`} onClick={() => openReport(r)} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">Open report <ArrowUpRight className="h-3 w-3" /></button></td>
                  </tr>
                );
              })}
              {reports.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No reports in governed corpus.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-slate-500">Source hash, report type and document-level processing status are supplied by the ari_source <span className="font-mono">annual_reports</span> / <span className="font-mono">report_documents</span> tables (pending delivery); rows above reflect the governed <span className="font-mono">report_instances</span>.</p>
      </Panel>
    </div>
  );
}
